import { Box, Typography } from '@mui/material'
import { keyframes } from '@mui/material/styles'
import { ResponsiveLine } from '@nivo/line'
import { FC } from 'react'
import { LiveOpsBlock } from '../../types/apiRequests'
import { border } from '../../layout/theme/border'
import { xxllncColor } from '../../layout/theme/color'
import { formatCompact } from '../../utils/formatNumber'
import { CompactGauge } from './CompactGauge'
import { fadeSlideIn } from './animations'

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
`

const card = {
  background: '#fff',
  borderRadius: border.card.radius,
  border: '1px solid rgba(0,0,0,0.06)',
}

const CAP = {
  fontSize: 11,
  fontWeight: 600,
  color: 'rgba(0,0,0,0.35)',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
}

const NUM_CELL = {
  fontSize: 13,
  textAlign: 'right' as const,
  fontVariantNumeric: 'tabular-nums' as const,
  color: 'rgba(0,0,0,0.7)',
}

const NAME_CELL = {
  fontSize: 13,
  fontWeight: 600,
  color: '#1a1a1a',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
}

const fmtDuration = (ms: number): string => {
  if (!ms) return '0s'
  if (ms < 1000) return `${Math.round(ms)}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.round(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`
}

const Tile: FC<{
  value: string
  label: string
  period: string
  accent: string
  delay: number
  pulsing?: boolean
  children?: React.ReactNode
}> = ({ value, label, period, accent, delay, pulsing, children }) => (
  <Box sx={{ ...card, p: 2.25, animation: `${fadeSlideIn} 0.4s ease-out ${delay}ms both` }}>
    <Typography sx={{ ...CAP, mb: 0.5 }}>{period}</Typography>
    <Typography
      sx={{
        fontSize: 30,
        fontWeight: 700,
        lineHeight: 1,
        color: accent,
        fontVariantNumeric: 'tabular-nums',
        animation: pulsing ? `${pulse} 1.6s ease-in-out infinite` : 'none',
      }}
    >
      {value}
    </Typography>
    <Typography sx={{ ...CAP, mt: 0.75, color: 'rgba(0,0,0,0.45)' }}>{label}</Typography>
    {children}
  </Box>
)

const Sparkline: FC<{ points: number[] }> = ({ points }) => {
  if (points.length === 0) return null
  const max = Math.max(...points, 1)
  const w = 100
  const h = 28
  const step = points.length > 1 ? w / (points.length - 1) : w
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${(h - (p / max) * h).toFixed(1)}`)
    .join(' ')
  return (
    <Box sx={{ mt: 1 }}>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={28} preserveAspectRatio="none">
        <path d={path} fill="none" stroke={xxllncColor.dark100} strokeWidth={1.5} />
      </svg>
    </Box>
  )
}

interface LiveOpsSectionProps {
  live: LiveOpsBlock | null | undefined
  translate: (key: string, options?: Record<string, unknown>) => string
}

export const LiveOpsSection: FC<LiveOpsSectionProps> = ({ live, translate }) => {
  if (!live) return null

  const buckets = live.traffic_last_hours ?? []
  const sparkPoints = buckets.map((b) => b.mcp ?? 0)
  const hourLabel = (iso: string) => `${new Date(iso).getHours().toString().padStart(2, '0')}:00`

  const lineData = [
    { id: 'MCP', data: buckets.map((b) => ({ x: hourLabel(b.hour), y: b.mcp ?? 0 })) },
    {
      id: translate('dashboard.live.executions'),
      data: buckets.map((b) => ({ x: hourLabel(b.hour), y: b.workflow ?? 0 })),
    },
  ]
  const lineColors = [xxllncColor.dark100, xxllncColor.orange100]

  const durations = live.workflow_durations ?? []

  return (
    <Box sx={{ mb: 3 }}>
      {/* Hero row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 2,
        }}
      >
        <Tile
          value={formatCompact(live.calls_last_hour ?? 0)}
          label={translate('dashboard.live.calls')}
          period={translate('dashboard.live.lastHour')}
          accent={xxllncColor.dark100}
          delay={0}
        >
          <Sparkline points={sparkPoints} />
        </Tile>
        <Tile
          value={formatCompact(live.workflow_executions_last_hour ?? 0)}
          label={translate('dashboard.live.executions')}
          period={translate('dashboard.live.lastHour')}
          accent={xxllncColor.orange100}
          delay={60}
        />
        <Tile
          value={String(live.active_users_now ?? 0)}
          label={translate('dashboard.live.activeUsers')}
          period={translate('dashboard.live.now')}
          accent={(live.active_users_now ?? 0) > 0 ? xxllncColor.green100 : xxllncColor.dark100}
          delay={120}
          pulsing={(live.active_users_now ?? 0) > 0}
        />
        <Box sx={{ ...card, animation: `${fadeSlideIn} 0.4s ease-out 180ms both` }}>
          <CompactGauge
            rate={live.error_rate_last_hour ?? 0}
            label={translate('dashboard.live.errorRate')}
            delay={180}
          />
        </Box>
      </Box>

      {/* 24h traffic line */}
      <Box sx={{ ...card, p: 2.5, mb: 2, animation: `${fadeSlideIn} 0.5s ease-out 220ms both` }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ ...CAP, color: 'rgba(0,0,0,0.4)' }}>
            {translate('dashboard.live.trafficTitle')}
          </Typography>
          <Typography sx={{ ...CAP }}>{translate('dashboard.live.last24h')}</Typography>
        </Box>
        <Box sx={{ height: 200 }}>
          <ResponsiveLine
            data={lineData}
            colors={lineColors}
            margin={{ top: 10, right: 16, bottom: 28, left: 40 }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: 0, max: 'auto' }}
            curve="monotoneX"
            enableArea
            areaOpacity={0.06}
            lineWidth={2}
            enablePoints={false}
            enableGridX={false}
            axisBottom={{ tickSize: 0, tickPadding: 8, tickRotation: 0, tickValues: 6 }}
            axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: 4 }}
            theme={{
              grid: { line: { stroke: 'rgba(0,0,0,0.04)' } },
              axis: { ticks: { text: { fill: 'rgba(0,0,0,0.3)', fontSize: 10, fontWeight: 500 } } },
            }}
            useMesh
            tooltip={({ point }) => (
              <Box sx={{ background: '#1a1a1a', color: '#fff', px: 1, py: 0.5, borderRadius: '8px', fontSize: 12 }}>
                {String(point.serieId)}: {String(point.data.yFormatted)}
              </Box>
            )}
          />
        </Box>
      </Box>

      {/* Workflow durations */}
      <Box sx={{ ...card, animation: `${fadeSlideIn} 0.5s ease-out 260ms both`, overflow: 'hidden' }}>
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: '1px solid rgba(0,0,0,0.04)',
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography sx={{ ...CAP, color: 'rgba(0,0,0,0.4)' }}>
            {translate('dashboard.live.durationsTitle')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2.5 }}>
            <Typography sx={{ fontSize: 13, color: 'rgba(0,0,0,0.6)', fontVariantNumeric: 'tabular-nums' }}>
              {translate('dashboard.live.avg')} <b>{fmtDuration(live.workflow_avg_ms ?? 0)}</b>
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'rgba(0,0,0,0.6)', fontVariantNumeric: 'tabular-nums' }}>
              p95 <b>{fmtDuration(live.workflow_p95_ms ?? 0)}</b>
            </Typography>
          </Box>
        </Box>
        {durations.length === 0 ? (
          <Box sx={{ px: 2.5, py: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, color: 'rgba(0,0,0,0.25)' }}>
              {translate('dashboard.live.noRuns')}
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 70px 80px 80px 70px',
                px: 2.5,
                py: 1,
                borderBottom: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              {[
                translate('dashboard.live.workflow'),
                translate('dashboard.live.runs'),
                translate('dashboard.live.avg'),
                'p95',
                translate('dashboard.live.errorsCol'),
              ].map((h, i) => (
                <Typography key={h} sx={{ ...CAP, fontSize: 10, textAlign: i > 0 ? 'right' : 'left' }}>
                  {h}
                </Typography>
              ))}
            </Box>
            {durations.map((d, i) => (
              <Box
                key={`${d.workflow_id ?? d.name}-${i}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 70px 80px 80px 70px',
                  px: 2.5,
                  py: 1.25,
                  borderBottom: i < durations.length - 1 ? '1px solid rgba(0,0,0,0.03)' : 'none',
                }}
              >
                <Typography sx={NAME_CELL}>{d.name}</Typography>
                <Typography sx={NUM_CELL}>{d.run_count ?? 0}</Typography>
                <Typography sx={NUM_CELL}>{fmtDuration(d.avg_ms ?? 0)}</Typography>
                <Typography sx={NUM_CELL}>{fmtDuration(d.p95_ms ?? 0)}</Typography>
                <Typography
                  sx={{
                    ...NUM_CELL,
                    fontWeight: 600,
                    color: (d.error_count ?? 0) > 0 ? xxllncColor.red100 : 'rgba(0,0,0,0.4)',
                  }}
                >
                  {d.error_count ?? 0}
                </Typography>
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  )
}
